import React from 'react';

import { Alert, Box, Button, InputAdornment, OutlinedInput, styled } from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import { capitalize, debounce, filter, set, uniqueId } from 'lodash';
import { MRT_SortingState, MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { venueActionAccessRequestApi } from '../../../../api/accessRequest/venueActionAccessRequestApi';
import { venueUnblockUserApi } from '../../../../api/users/venueUnblockUserApi';
import {
  BlockedUser,
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
  VenueAccessREnum,
  VenueListAccessRequetsFilter,
  VenueListVenueBlockedUsersRequest,
} from '../../../../graphql/API';
import useAccessRequestQuery from '../../../../hooks/reactQuery/useAccessRequestQuery';
import useListBlockedUserQuery from '../../../../hooks/reactQuery/useListBlockedUserQuery';
import useVenueId from '../../../../hooks/useVenueId';
import Icons from '../../../../themes/icons';
import ChipOptions from '../../../ChipOptions/ChipOptions';
import { OnAction, isVenueActionAccessInput } from '../../../List/ListAccess/types';
import { getSuccessMessage } from '../../ListAccessRequest/components/helper';
import { TabOption } from '../../ListAccessRequest/components/types';
import { accessRequestsTableColumns, blockedUsersTableColumns } from './columns';

const RemoveUsersButton = styled(Button)({
  borderRadius: '8px',
  backgroundColor: '#E05757',
  color: '#fff',
  padding: '8px 32px',
  border: '1px solid #E05757',
  ':hover': {
    color: '#E05757',
    backgroundColor: '#fff',
    border: '1px solid #E05757',
  },
});

const statusOptions = [
  { label: capitalize(TabOption.accepted), value: TabOption.accepted },
  { label: 'Rejected', value: TabOption.rejected },
  { label: capitalize(TabOption.blocked), value: TabOption.blocked },
];

export const ListAccessRequestsContainer = () => {
  const queryClient = useQueryClient();

  const venueID = useVenueId();

  const [filters, setFilters] = React.useState<Partial<VenueListAccessRequetsFilter>>({
    status: UserAccessRequestResultValue.accepted,
  });

  const [blockedUsersFilters, setBlockedUsersFilters] = React.useState<
    Partial<VenueListVenueBlockedUsersRequest>
  >({});

  const accessRequestsQueryResult = useAccessRequestQuery(
    filters,
    (filters.status as string) === 'blocked',
  );
  const { isFetching: accessRequestIsFetching, fetchNextPage: accessRequestFetchNextPage } =
    accessRequestsQueryResult;

  const {
    data: blockedUsersData,
    isFetching: blockedUsersIsFetching,
    isLoading: blockedUsersIsLoading,
    fetchNextPage: blockedUsersFetchNextPage,
    hasNextPage: blockedUsersHasNextPage,
  } = useListBlockedUserQuery(blockedUsersFilters, (filters.status as string) !== 'blocked');

  const [isBulkUpdatingStarted, setIsBulkUpdatingStarted] = React.useState(false);
  const [dictSuccessText, setDictSuccessText] = React.useState<
    Record<string, React.ReactNode | null>
  >({});
  const [accessRequestLoadingState, setAccessRequestLoadingState] = React.useState<
    Record<string, boolean>
  >({});
  const [sorting, setSorting] = React.useState<MRT_SortingState>([
    {
      id: 'userName',
      desc: true,
    },
  ]);
  const [blockedUsersSorting, setBlockedUsersSorting] = React.useState<MRT_SortingState>([
    {
      id: 'userName',
      desc: true,
    },
  ]);
  const [selectedRows, setSelectedRows] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const [sort] = sorting;
    setFilters((x) => ({ ...x, sortBy: sort?.id, sortDirection: sort?.desc ? 'desc' : 'asc' }));
  }, [sorting]);

  React.useEffect(() => {
    const [sort] = blockedUsersSorting;
    setBlockedUsersFilters((x) => ({
      ...x,
      sortBy: sort?.id,
      sortDirection: sort?.desc ? 'desc' : 'asc',
    }));
  }, [blockedUsersSorting]);

  React.useEffect(() => {
    setSelectedRows({});
  }, [filters.status]);

  const handleSearch = debounce((value) => {
    if ((filters.status as string) === 'blocked') {
      setBlockedUsersFilters((prevFilters) => ({
        ...prevFilters,
        searchText: value,
      }));
    } else {
      setFilters((prevFilters) => ({
        ...prevFilters,
        searchText: value,
      }));
    }
  }, 500);

  const handleChange = React.useCallback(
    (value?: string) => {
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleAction = React.useCallback<OnAction>(
    async (input) => {
      if (!input) {
        return;
      }
      const isVenueAccessAction = isVenueActionAccessInput(input);
      const key = isVenueAccessAction
        ? `${input.userID}:${input.offerID}`
        : `${input.userID}:${input.venueID}`;

      setAccessRequestLoadingState((x) => ({ ...x, [key]: true }));

      if (isVenueAccessAction) {
        await venueActionAccessRequestApi(input);
      } else {
        await venueUnblockUserApi(input);
      }

      const queryKey = isVenueAccessAction ? 'list-access-request' : 'list-blocked-users';

      await queryClient.invalidateQueries({ queryKey: [queryKey] });

      setDictSuccessText({
        [filters.status ?? '']: getSuccessMessage(
          String(filters.status) as TabOption,
          isVenueAccessAction ? accessRequestsQueryResult.data : blockedUsersData,
          input,
        ),
      });
      setAccessRequestLoadingState((x) => ({ ...x, [key]: false }));
    },
    [accessRequestsQueryResult.data, blockedUsersData, filters.status, queryClient],
  );

  const bulkAccessRequestsAction = async () => {
    try {
      setAccessRequestLoadingState({});
      const selectedRowsKeys = Object.keys(selectedRows);
      const selectedRowsData = accessRequestsQueryResult.data?.filter((item) =>
        selectedRowsKeys.includes(`${item.userID}-${item.offerID}`),
      );
      const blockedUsersSelectedRowsData = blockedUsersData.filter((item) =>
        selectedRowsKeys.includes(`${item.id}-${venueID}`),
      );

      setIsBulkUpdatingStarted(true);

      setAccessRequestLoadingState((prev) => ({
        ...prev,
        ...Object.fromEntries(selectedRowsKeys.map((key) => [key, true])),
      }));

      if ((filters.status as string) === 'blocked') {
        if (blockedUsersSelectedRowsData) {
          await Promise.all(
            blockedUsersSelectedRowsData.map((item) => {
              if (!item.id) {
                return;
              }
              return handleAction({
                userID: Number(item.id),
                venueID: Number(venueID),
              });
            }),
          );

          setAccessRequestLoadingState((prev) => ({
            ...prev,
            ...Object.fromEntries(selectedRowsKeys.map((key) => [key, false])),
          }));

          setIsBulkUpdatingStarted(false);
        }
        setSelectedRows({});
      } else {
        if (selectedRowsData) {
          await Promise.all(
            selectedRowsData.map((item) => {
              if (!item.userID || !item.offerID) {
                return;
              }
              return handleAction({
                action:
                  filters.status === UserAccessRequestResultValue.accepted
                    ? VenueAccessREnum.declined
                    : VenueAccessREnum.accepted,
                userID: item.userID,
                offerID: item.offerID,
              });
            }),
          );

          setAccessRequestLoadingState((prev) => ({
            ...prev,
            ...Object.fromEntries(selectedRowsKeys.map((key) => [key, false])),
          }));

          setIsBulkUpdatingStarted(false);
        }
        setSelectedRows({});
      }
    } catch (error) {
      setIsBulkUpdatingStarted(false);
      setAccessRequestLoadingState((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.keys(selectedRows).map((key) => [key, false])),
      }));
      setSelectedRows({});
      console.error('Error performing bulk action:', error);
    }
  };

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !accessRequestIsFetching &&
          accessRequestsQueryResult.hasNextPage
        ) {
          accessRequestFetchNextPage();
        }
      }
    },
    [accessRequestIsFetching, accessRequestsQueryResult.hasNextPage],
  );

  const fetchMoreOnBottomReachedOfBlockedUsers = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !blockedUsersIsFetching &&
          blockedUsersHasNextPage
        ) {
          blockedUsersFetchNextPage();
        }
      }
    },
    [blockedUsersIsFetching, blockedUsersHasNextPage],
  );

  const accessRequestsTable = useMaterialReactTable({
    enableRowSelection: true,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedRows,
    muiTableHeadRowProps: { sx: { backgroundColor: '#fff' } },
    muiTableBodyRowProps: ({
      row: {
        original: { offerID, userID },
      },
    }) => ({
      sx: {
        backgroundColor: selectedRows[`${userID}-${offerID}`] ? '#fafafa' : 'inherit',
        cursor: 'pointer',
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        fontWeight: 400,
        color: '#848484',
        borderColor: custom.colors.brandLightGrey,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none', backgroundColor: '#fff' } },
    muiTableContainerProps: {
      sx: {
        margin: '8px 0',
        height: '565px',
        maxHeight: '565px',
        backgroundColor: '#fff',
      },
      onScroll: (event) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    muiFilterCheckboxProps: {
      color: 'secondary',
    },
    muiSelectCheckboxProps: {
      sx: {
        color: '#E0E0E0',
        '&.Mui-checked': {
          color: '#765BC6',
        },
      },
    },
    columns: accessRequestsTableColumns({
      loadingState: accessRequestLoadingState,
      onAction: handleAction,
    }),
    data: (accessRequestsQueryResult.data as VennueAccessRequestItem[]) || [],
    rowVirtualizerOptions: { overscan: 5 },
    state: {
      isLoading: accessRequestIsFetching || accessRequestsQueryResult.isLoading,
      rowSelection: selectedRows,
      sorting,
    },
    getRowId: (row) =>
      row.offerID && row.userID
        ? String(`${row.userID}-${row.offerID}`)
        : uniqueId('access-requests'),
  });

  const blockedUsersTable = useMaterialReactTable({
    enableRowSelection: true,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setBlockedUsersSorting,
    onRowSelectionChange: setSelectedRows,
    muiTableHeadRowProps: { sx: { backgroundColor: '#fff' } },
    muiTableBodyRowProps: ({
      row: {
        original: { id },
      },
    }) => ({
      sx: {
        backgroundColor: selectedRows[`${id}-${venueID}`] ? '#fafafa' : 'inherit',
        cursor: 'pointer',
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        fontWeight: 400,
        color: '#848484',
        borderColor: custom.colors.brandLightGrey,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none', backgroundColor: '#fff' } },
    muiTableContainerProps: {
      sx: {
        margin: '8px 0',
        height: '565px',
        maxHeight: '565px',
        backgroundColor: '#fff',
      },
      onScroll: (event) => fetchMoreOnBottomReachedOfBlockedUsers(event.target as HTMLDivElement),
    },
    muiFilterCheckboxProps: {
      color: 'secondary',
    },
    muiSelectCheckboxProps: {
      sx: {
        color: '#E0E0E0',
        '&.Mui-checked': {
          color: '#765BC6',
        },
      },
    },
    columns: blockedUsersTableColumns({
      venueID: Number(venueID),
      loadingState: accessRequestLoadingState,
      onAction: handleAction,
    }),
    data: (blockedUsersData as BlockedUser[]) || [],
    rowVirtualizerOptions: { overscan: 5 },
    state: {
      isLoading: blockedUsersIsFetching || blockedUsersIsLoading,
      rowSelection: selectedRows,
      sorting: blockedUsersSorting,
    },
    getRowId: (row) => (row.id ? String(`${row.id}-${venueID}`) : uniqueId('blocked-users')),
  });

  const successText = React.useMemo(
    () => dictSuccessText[String(filters.status) as TabOption],
    [dictSuccessText, filters.status],
  );

  return (
    <Box position={'relative'}>
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <ChipOptions
          defaultValue={filters.status || UserAccessRequestResultValue.accepted}
          name='status'
          options={statusOptions}
          onChange={({ value: v }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              status: v as UserAccessRequestResultValue,
            }));
          }}
        />
        <OutlinedInput
          id='outlined-adornment-amount'
          placeholder='Search a member...'
          startAdornment={
            <InputAdornment position='start'>
              <Icons.SearchIcon />
            </InputAdornment>
          }
          onChange={(e) => {
            handleChange(e.target.value);
          }}
          size='small'
          sx={{ minWidth: '340px' }}
        />
      </Box>

      {(filters.status as string) !== 'blocked' && (
        <MaterialReactTable table={accessRequestsTable} />
      )}

      {(filters.status as string) === 'blocked' && <MaterialReactTable table={blockedUsersTable} />}

      {Object.values(selectedRows).length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          {(filters.status as string) === 'blocked' ? (
            <Button
              variant='contained'
              color='primary'
              sx={{ padding: '8px 32px', borderRadius: '8px' }}
              onClick={bulkAccessRequestsAction}
              disabled={isBulkUpdatingStarted}
            >
              Unblock selected users ({Object.values(selectedRows).length})
            </Button>
          ) : filters.status === UserAccessRequestResultValue.accepted ? (
            <RemoveUsersButton onClick={bulkAccessRequestsAction} disabled={isBulkUpdatingStarted}>
              Remove selected users ({Object.values(selectedRows).length})
            </RemoveUsersButton>
          ) : filters.status === UserAccessRequestResultValue.declined ? (
            <Button
              variant='contained'
              color='primary'
              sx={{ padding: '8px 32px', borderRadius: '8px' }}
              onClick={bulkAccessRequestsAction}
              disabled={isBulkUpdatingStarted}
            >
              Undo for selected users ({Object.values(selectedRows).length})
            </Button>
          ) : null}
        </Box>
      )}

      {Object.values(selectedRows).length <= 0 && successText && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '12px',
            left: '0',
          }}
        >
          <Alert severity='success'>{successText}</Alert>
        </Box>
      )}
    </Box>
  );
};
